import React from 'react'
import { useDispatch } from 'react-redux'
import { List, ListItem } from '@mobi/component-library/Base/List'
import { appHistory } from '@core/Utils/routing/history'
import { OverlayClose } from '@core/Components/Overlay'
import type { SearchResult } from '@core/Areas/Search/types'
import {
  isSearchResultAcceptor,
  isSearchResultFobEvent,
  isSearchResultFobProposition,
  isSearchResultRace,
} from '@core/Areas/Search/helpers/typeGuards'
import { addSearchTermToPrevious } from '@core/Areas/Search/Store'
import { NoSearchResults } from '../Common/NoSearchResults'
import { EmptyScreen } from '../Common/EmptyScreen'
import { SearchErrored } from '../Common/SearchErrored'
import { LoadingSkeleton } from '../Common/LoadingSkeleton'
import { getRedirectUrl } from './helpers/getRedirectUrl'
import { RacingSearchResult } from './Components/RacingSearchResult'

export const RacingSearch: React.FC<{
  isError: boolean
  isLoading: boolean
  searchTerm: string
  handleImmediateSearch: (searchTerm: string) => void
  searchResults?: SearchResult[]
}> = ({ isError, isLoading, searchResults, searchTerm, handleImmediateSearch }) => {
  const dispatch = useDispatch()

  if (isError) {
    return <SearchErrored />
  }

  if (isLoading) {
    return <LoadingSkeleton />
  }

  const searchResultsToRender = searchResults?.filter(filterForKnownSearchResult)
  const hasNoResults = searchResultsToRender?.length === 0

  if (!searchResultsToRender || (hasNoResults && searchTerm.length < 4)) {
    return <EmptyScreen searchMode='racing' handleImmediateSearch={handleImmediateSearch} />
  }

  if (hasNoResults) {
    return <NoSearchResults />
  }

  const handleResultClick = (searchResult: SearchResult) => {
    dispatch(addSearchTermToPrevious({ type: 'racing', searchTerm }))
    appHistory.push(getRedirectUrl(searchResult))
    OverlayClose()
  }

  return (
    <List variant='card' itemSize='dynamic'>
      {searchResultsToRender.map(searchResult => (
        <ListItem
          key={searchResult.id}
          onClick={() => handleResultClick(searchResult)}
          data-tid-search-result={`${searchResult.kind.toLowerCase()}-${searchResult.id}`}
          content={
            <RacingSearchResult
              searchResult={searchResult}
              highlights={searchResult.metadata.highlights}
            />
          }
        />
      ))}
    </List>
  )
}

// =============
// Local Helpers
// =============

function filterForKnownSearchResult(searchResult: SearchResult): boolean {
  return (
    isSearchResultAcceptor(searchResult) ||
    isSearchResultRace(searchResult) ||
    isSearchResultFobEvent(searchResult) ||
    isSearchResultFobProposition(searchResult)
  )
}
